import type { Guide } from "../types";

// ── Apps added to the store since the first help-center release ─────────────
// Page Scroll, News Scroller, Air Quality, QR Code, Meet the Team, Bell
// Schedule, Power BI, Wayfinding and Hotel Welcome Board, plus the six news
// apps (Headline News … Sports News). Same shape as the other app guides — open
// the store, pick the app, configure it, save it, then assign it to a screen.
// `group` mirrors the store category of each card.

// ── News apps ────────────────────────────────────────────────────────────────
// Each news app is an RSS Feed preset (admin-web pages/rss-feed/newsPresets.ts):
// the same editor, a curated list of sources, and the topic's accent colour.
// The editor screenshot (02) is cropped to the settings panel on purpose: the
// live preview shows real headlines and article photos.
function newsApp(o: {
  slug: string;
  title: string;
  summary: string;
  topic: string;
  colour: string;
  sources: string[];
  keywords: string[];
}): Guide {
  return {
    slug: o.slug,
    category: "apps",
    route: `/apps/${o.slug}`,
    title: o.title,
    summary: o.summary,
    intro: `${o.title} shows the latest ${o.topic} headlines as rotating full-screen cards or a scrolling ticker. It's the RSS Feed app with a ready-made list of public news feeds, so you pick a source instead of hunting for a feed address.`,
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: `Click Apps in the sidebar, open the App Store tab, and find ${o.title} in the Live Feeds category. Click the card to open the editor.`,
          },
        ],
        screenshot: {
          src: `apps/${o.slug}/01.png`,
          alt: `The App Store on the Live Feeds category with the ${o.title} card highlighted`,
          todo: `App Store tab, Live Feeds chip, ${o.title} card highlighted.`,
        },
      },
      {
        title: "Choose a news source",
        blocks: [
          {
            type: "p",
            text: `The editor opens as New ${o.title}, with the Feed name already filled in — rename it if you like. Under News source pick one of the feeds below; the first one is selected for you:`,
          },
          { type: "ul", items: o.sources },
          {
            type: "p",
            text: "Prefer another site? Choose Custom feed URL… and paste its address into RSS feed URL. Leave Source title blank to show the feed's own name, or type your own. The Preview on the right loads the latest headlines.",
          },
        ],
        screenshot: {
          src: `apps/${o.slug}/02.png`,
          alt: `The New ${o.title} editor with Feed name, News source, RSS feed URL and Source title`,
          caption: "Pick a source from the News source list.",
          todo: `/rss-feed?create=1 from the ${o.title} card: settings panel only (Feed name, News source, RSS feed URL, Source title, Zone size, Display mode, Card layout). Cropped on purpose — the live preview shows real headlines and photos.`,
        },
      },
      {
        title: "Set the layout",
        blocks: [
          {
            type: "p",
            text: `${o.title} starts with Cards, the Broadcast card layout, 12 items, a ${o.colour} accent colour, and Refresh every set to 10 minutes. Change any of them: Display mode (Cards or Ticker, also per Zone size), Card layout, Text direction, Number of items to show, what to Show, the QR code, Colors and Refresh every. These work exactly as in the RSS Feed app.`,
          },
          { type: "steps-ref", label: "See: RSS Feed — layout and style options", to: "/apps/rss-feed" },
        ],
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: `Click Save Changes. The app appears on the My Apps tab as ${o.title}. Assign it to a screen, or add it to a playlist or a channel zone — a Ticker fits a strip along the bottom of a channel.`,
          },
          { type: "steps-ref", label: "See: Assign content to a screen", to: "/screens/assign-content" },
          {
            type: "note",
            tone: "info",
            text: "If the screen goes offline, it keeps showing the last headlines it downloaded and refreshes when the connection is back.",
          },
        ],
      },
    ],
    related: [
      { label: "RSS Feed & news apps", to: "/apps/rss-feed" },
      { label: "News Scroller", to: "/apps/news-scroller" },
      { label: "How apps work", to: "/apps/add-an-app" },
    ],
    keywords: ["news", "headlines", "rss", "feed", ...o.keywords],
  };
}

const NEWS_APPS: Guide[] = [
  newsApp({
    slug: "headline-news",
    title: "Headline News",
    summary: "Top world headlines from a news source you choose.",
    topic: "world",
    colour: "red",
    sources: ["BBC News — World", "Al Jazeera — All news", "NPR — News", "The Guardian — World"],
    keywords: ["headline news", "world news", "top stories", "breaking news"],
  }),
  newsApp({
    slug: "business-finance-news",
    title: "Business & Finance News",
    summary: "Business and markets headlines from a news source you choose.",
    topic: "business and finance",
    colour: "green",
    sources: ["BBC News — Business", "CNBC — Top news", "Yahoo Finance", "The Guardian — Business"],
    keywords: ["business news", "finance news", "markets", "economy"],
  }),
  newsApp({
    slug: "entertainment-news",
    title: "Entertainment News",
    summary: "Film, TV, music and culture headlines from a news source you choose.",
    topic: "entertainment and culture",
    colour: "rose",
    sources: [
      "BBC News — Entertainment & Arts",
      "Variety",
      "The Hollywood Reporter",
      "Sky News — Entertainment",
      "The Guardian — Culture",
      "NPR — Movies",
    ],
    keywords: ["entertainment news", "movies", "film", "tv", "music", "culture", "celebrity"],
  }),
  newsApp({
    slug: "technology-news",
    title: "Technology News",
    summary: "Tech headlines from a news source you choose.",
    topic: "technology",
    colour: "blue",
    sources: ["BBC News — Technology", "The Verge", "TechCrunch", "Ars Technica", "WIRED"],
    keywords: ["technology news", "tech news", "gadgets", "startups"],
  }),
  newsApp({
    slug: "natural-world-news",
    title: "Natural World News",
    summary: "Science, nature and environment headlines from a news source you choose.",
    topic: "science, nature and environment",
    colour: "green",
    sources: [
      "BBC News — Science & Environment",
      "The Guardian — Environment",
      "ScienceDaily — Top science",
      "Phys.org",
    ],
    keywords: ["natural world news", "nature", "environment", "science news", "climate"],
  }),
  newsApp({
    slug: "sports-news",
    title: "Sports News",
    summary: "Sports headlines from a news source you choose.",
    topic: "sports",
    colour: "orange",
    sources: ["BBC Sport", "ESPN — Top headlines", "Sky Sports — Football", "The Guardian — Sport"],
    keywords: ["sports news", "sport", "football", "scores"],
  }),
];

export const appsNewGuides: Guide[] = [
  // ── Basic ──────────────────────────────────────────────────────────────────
  {
    slug: "page-scroll",
    category: "apps",
    route: "/apps/page-scroll",
    title: "Page Scroll",
    summary: "Scroll a long web page slowly from top to bottom, on a loop.",
    intro:
      "Page Scroll shows a web page — a news page, a menu, a results table — and scrolls it slowly from top to bottom, pauses, and starts again. It suits pages that are longer than the screen and would otherwise only ever show their top.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Page Scroll in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/page-scroll/01.png",
          alt: "The app store with Page Scroll in the Basic group",
          todo: "Apps store, Basic category, Page Scroll card highlighted.",
        },
      },
      {
        title: "Enter the page and how to load it",
        blocks: [
          {
            type: "p",
            text: "In Create: Page Scroll, type the page’s Address (https:// is added for you). Under How to load it, keep Through PulsePanels (recommended): PulsePanels fetches the page, measures its real length, and it works on many sites that refuse to be embedded. Direct from the site embeds the page as-is — then you must set the Page height yourself, and sites that block embedding show a blank screen.",
          },
        ],
        screenshot: {
          src: "apps/page-scroll/02.png",
          alt: "The Page Scroll editor with the Address and How to load it fields and a live preview",
          todo: "Page Scroll editor open: Page section (Address, How to load it) + preview scrolling a page.",
        },
      },
      {
        title: "Set the motion and appearance",
        blocks: [
          {
            type: "p",
            text: "Under Motion set the Speed (pixels per second), what happens At the bottom (Jump back to the top, or Scroll back up), and the Pause at the top and Pause at the bottom in seconds. Under Appearance set the Zoom — below 100% fits more of the page across — the Background, how often to Reload the page (minutes), and the Orientation.",
          },
        ],
        screenshot: {
          src: "apps/page-scroll/03.png",
          alt: "Motion and Appearance settings for Page Scroll",
          caption: "Tune the speed and pauses, then the zoom.",
          todo: "Page Scroll editor scrolled to Motion (Speed, At the bottom, pauses) and Appearance (Zoom, Reload).",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Website URL", to: "/apps/website-url" },
    ],
    keywords: ["page scroll", "scrolling", "website", "web page", "auto scroll", "long page"],
  },

  // ── Live Feeds ─────────────────────────────────────────────────────────────
  {
    slug: "air-quality",
    category: "apps",
    route: "/apps/air-quality",
    title: "Air Quality",
    summary: "Show the live air-quality index for a city, with pollutants and health advice.",
    intro:
      "Air Quality shows the current air-quality index for a location as a big, colour-coded reading, with optional pollutant levels and health advice, in English or Arabic.",
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Air Quality in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/air-quality/01.png",
          alt: "The app store with Air Quality in the Live Feeds group",
          todo: "Apps store, Live Feeds category, Air Quality card highlighted.",
        },
      },
      {
        title: "Choose the location",
        blocks: [
          {
            type: "p",
            text: "In Create: Air Quality, under Location use Search for a city and pick the right match. Shown on screen is the name the board displays — shorten it if you like. The Coordinates are filled in for you.",
          },
        ],
        screenshot: {
          src: "apps/air-quality/02.png",
          alt: "The Air Quality editor with the city search and a live preview",
          todo: "Air Quality editor open: Location section + preview showing a reading.",
        },
      },
      {
        title: "Set the reading and appearance",
        blocks: [
          {
            type: "p",
            text: "Under Reading pick the Index — European AQI (EEA) or US AQI (EPA) — the Language, and the Layout: Big reading only, or Reading + pollutants. Choose which Pollutants to list, and whether to Show health advice and Show last-updated time. Set Refresh every (minutes).",
          },
          {
            type: "p",
            text: "Under Appearance set the Background, Text and Accent colours, and keep Colour the reading by its band on for the official green-to-purple palette.",
          },
          {
            type: "note",
            tone: "info",
            text: "The source publishes hourly, so refreshing more often than every 30 minutes gains nothing.",
          },
        ],
        screenshot: {
          src: "apps/air-quality/03.png",
          alt: "Index, Layout, Pollutants and Appearance settings for Air Quality",
          caption: "Pick the index and layout, then the colours.",
          todo: "Air Quality editor scrolled to Reading (Index, Layout, Pollutants) and Appearance.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Weather", to: "/apps/weather" },
    ],
    keywords: ["air quality", "aqi", "pollution", "pm2.5", "health", "environment"],
  },

  {
    slug: "news-scroller",
    category: "apps",
    route: "/apps/news-scroller",
    title: "News Scroller",
    summary: "A scrolling ticker of your own headlines, with a title or clock.",
    intro:
      "News Scroller is a ticker bar that scrolls headlines you type across the screen, with a header showing a title or the date and time. It’s made for a strip zone along the bottom of a channel.",
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find News Scroller in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/news-scroller/01.png",
          alt: "The app store with News Scroller in the Live Feeds group",
          todo: "Apps store, Live Feeds category, News Scroller card highlighted.",
        },
      },
      {
        title: "Set the header",
        blocks: [
          {
            type: "p",
            text: "Type a name for the app at the top of the page. The Live preview shows the ticker as you edit. Under Header choose the Header content — Title, or Date & Time (with a Format and a 12-hour or 24-hour Clock) — and turn Show header on or off. Pick a Style (Classic, Modern, Bold or Stylish) and a Header icon.",
          },
        ],
        screenshot: {
          src: "apps/news-scroller/02.png",
          alt: "The News Scroller editor with the live preview and Header settings",
          todo: "News Scroller page: Live preview ticker + Header card.",
        },
      },
      {
        title: "Style it and add headlines",
        blocks: [
          {
            type: "p",
            text: "Under Appearance pick the Font, the Background, Header color, Text color and Accent / dots colours, and the Scroll speed (seconds per loop — lower is faster). Under News items type your headlines and use + Add headline for more.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Want headlines pulled from a news website instead? Use RSS Feed or one of the news apps.",
          },
        ],
        screenshot: {
          src: "apps/news-scroller/03.png",
          alt: "Appearance and News items sections of the News Scroller editor",
          caption: "Set the colours and speed, then type your headlines.",
          todo: "News Scroller page scrolled to Appearance + News items with three headlines.",
        },
      },
      {
        title: "Create it and place it on a screen",
        blocks: [
          {
            type: "p",
            text: "Click Create (Save Changes when editing). Put the ticker in a strip zone of a channel so it runs under your main content, or assign it to a screen on its own.",
          },
          {
            type: "steps-ref",
            label: "See: Create a channel",
            to: "/content/create-a-channel",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "RSS Feed & news apps", to: "/apps/rss-feed" },
      { label: "Create a channel", to: "/content/create-a-channel" },
    ],
    keywords: ["news scroller", "ticker", "headlines", "scrolling text", "crawl", "breaking news"],
  },

  ...NEWS_APPS,

  // ── Social ─────────────────────────────────────────────────────────────────
  {
    slug: "meet-the-team",
    category: "apps",
    route: "/apps/meet-the-team",
    title: "Meet the Team",
    summary: "Introduce your staff — or celebrate students — with photos, roles and badges.",
    intro:
      "Meet the Team puts faces to names: a showcase with one person featured, a grid of the whole team, or a spotlight that rotates through people one at a time. Switch it to Students to recognise awards and achievements instead. Import a whole roster from a spreadsheet in one go.",
    readingTime: 3,
    status: "draft",
    group: "Social",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Meet the Team in the Social category.",
          },
        ],
        screenshot: {
          src: "apps/meet-the-team/01.png",
          alt: "The app store with Meet the Team in the Social group",
          todo: "Apps store, Social category, Meet the Team card highlighted.",
        },
      },
      {
        title: "Choose who the board is about",
        blocks: [
          {
            type: "p",
            text: "In Create: Meet the Team, under Heading pick This board is about: Staff — your team by name and role, or Students — recognition, awards and achievements. The field names follow your choice (for students, “Class or year” and “House or year group”). Set the Language (English, Arabic, or both), the Title and an optional Subtitle.",
          },
        ],
        screenshot: {
          src: "apps/meet-the-team/02.png",
          alt: "The Meet the Team editor with the Heading section and a live preview",
          todo: "Meet the Team editor open: Heading (This board is about, Language, Title) + showcase preview with placeholder people.",
        },
      },
      {
        title: "Add people — by hand or from a CSV",
        blocks: [
          {
            type: "p",
            text: "Use + Add person for each person: Name, Role, Department, an optional Badge and Badge style, a Quote (shown when they’re featured) and a Photo. Untick Show to hide someone without deleting them.",
          },
          {
            type: "p",
            text: "For a larger roster, click Download CSV template, fill it in with Excel, then Import from CSV — the rows are added to the list.",
          },
        ],
        screenshot: {
          src: "apps/meet-the-team/03.png",
          alt: "The people list with Download CSV template, Import from CSV and Add person",
          caption: "Add people one by one, or import a roster.",
          todo: "Meet the Team editor scrolled to the People section with Download CSV template / Import from CSV and one person expanded. Use placeholder names only.",
        },
      },
      {
        title: "Pick the layout",
        blocks: [
          {
            type: "p",
            text: "Under Appearance choose the Layout — Showcase (a grid with one person featured), Grid (the whole team) or Spotlight (one person at a time) — the Orientation, how many people per page, and the seconds per page or per person. Optionally group by department, add a Tagline in the Stats strip, and set Show roles, Show badges, Round photos and the colours.",
          },
        ],
        screenshot: {
          src: "apps/meet-the-team/04.png",
          alt: "Layout, Stats strip and styling options for Meet the Team",
          todo: "Meet the Team editor scrolled to Appearance with Layout set to Grid.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Bell Schedule", to: "/apps/bell-schedule" },
    ],
    keywords: [
      "meet the team",
      "staff",
      "team",
      "students",
      "recognition",
      "employee of the month",
      "csv",
      "roster",
    ],
  },

  // ── Communication ──────────────────────────────────────────────────────────
  {
    slug: "qr-code",
    category: "apps",
    route: "/apps/qr-code",
    title: "QR Code",
    summary: "A large, scannable QR code for a link, Wi-Fi, text or phone number.",
    intro:
      "The QR Code app fills a screen (or a zone) with a clean, scannable QR code and a short call to action — send visitors to a web page, join them to the guest Wi-Fi, show a message, or start a call.",
    readingTime: 2,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find QR Code in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/qr-code/01.png",
          alt: "The app store with QR Code in the Communication group",
          todo: "Apps store, Communication category, QR Code card highlighted.",
        },
      },
      {
        title: "Choose what the code opens",
        blocks: [
          {
            type: "p",
            text: "In Create: QR Code, under Code content pick the Type: Website link, Wi-Fi network (Network name, Security and Password), Plain text, or Phone number. For a link, https:// is added for you.",
          },
        ],
        screenshot: {
          src: "apps/qr-code/02.png",
          alt: "The QR Code editor with the Code content type and a live preview",
          todo: "QR Code editor open: Code content (Type = Website link) + preview.",
        },
      },
      {
        title: "Add wording and style it",
        blocks: [
          {
            type: "p",
            text: "Under Wording set a Title, Subtitle and Call to action. Under Appearance choose the Layout (Centred, Text + code, or Poster band), Code size and colours — keep White plate behind the code on, as cameras need the border. You can add a Centre logo — Error correction is then set to Highest automatically, because the logo covers part of the code.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Scan the preview with your phone before you save.",
          },
        ],
        screenshot: {
          src: "apps/qr-code/03.png",
          alt: "Wording, Appearance and Logo settings for the QR Code",
          caption: "Add a clear call to action, then style the code.",
          todo: "QR Code editor scrolled to Wording + Appearance + Logo.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen or a zone of a channel.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Follow Us", to: "/apps/follow-us" },
    ],
    keywords: ["qr code", "qr", "wifi", "link", "scan", "phone", "call to action"],
  },

  // ── Business ───────────────────────────────────────────────────────────────
  {
    slug: "bell-schedule",
    category: "apps",
    route: "/apps/bell-schedule",
    title: "Bell Schedule",
    summary: "A school timetable board — the period now, the countdown to the bell, and the day.",
    intro:
      "Bell Schedule shows the class period running now, a countdown to the next bell, and the rest of the day. Build one schedule per kind of day, assign them to weekdays, and the board follows each screen’s own timezone.",
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Bell Schedule in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/bell-schedule/01.png",
          alt: "The app store with Bell Schedule in the Business group",
          todo: "Apps store, Business category, Bell Schedule card highlighted.",
        },
      },
      {
        title: "Set the heading",
        blocks: [
          {
            type: "p",
            text: "In Create: Bell Schedule, under Heading set the Language (English, Arabic, or both), the Title, and the Timezone — leave Follow the screen so each screen uses its own time.",
          },
        ],
        screenshot: {
          src: "apps/bell-schedule/02.png",
          alt: "The Bell Schedule editor with the Heading section and a live preview",
          todo: "Bell Schedule editor open: Heading + preview showing the current period and countdown.",
        },
      },
      {
        title: "Build schedules and assign weekdays",
        blocks: [
          {
            type: "p",
            text: "A schedule is a kind of day — “Regular Day”, “Assembly”, “Half Day”. For each, set the Schedule name and use Add period and Add break; each has a Name, Starts and Ends time, and an optional Note. Use + Add schedule for another kind of day, or duplicate one to make a variant.",
          },
          {
            type: "p",
            text: "Under Week choose which schedule each weekday runs, or No classes. Set the text shown When there are no classes and After the last bell.",
          },
        ],
        screenshot: {
          src: "apps/bell-schedule/03.png",
          alt: "Schedules with periods and breaks, and the Week section",
          caption: "Build each kind of day, then map weekdays to it.",
          todo: "Bell Schedule editor scrolled to Schedules (periods + a break) and Week.",
        },
      },
      {
        title: "Choose the layout and check it",
        blocks: [
          {
            type: "p",
            text: "Under Appearance pick the Layout — Board (the whole day, with the current period marked) or Focus (the current period and the countdown only) — the Orientation, the Clock format, and whether to Show the clock, Show the countdown and Show notes. Use Preview day and Preview time under the preview to check any moment of the week.",
          },
        ],
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to your corridor or classroom screens.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Meet the Team", to: "/apps/meet-the-team" },
    ],
    keywords: ["bell schedule", "timetable", "school", "periods", "countdown", "class", "education"],
  },

  {
    slug: "clinic-door",
    category: "apps",
    route: "/apps/clinic-door",
    title: "Clinic Door",
    summary: "The screen at a clinic's door: the doctor, the clinic's status, the ticket being served, the next patients and any delay.",
    intro:
      "Clinic Door shows one clinic from Clinic Flow on the screen at its door, in English, Arabic or both. It follows the clinic live: when your hospital system or a nurse calls the next patient, the door highlights the ticket and can play a tone and an announcement. Patients appear only as ticket numbers.",
    prerequisites: [
      "The Clinic Flow module, with the clinic set up on the Clinics page or by your hospital system.",
    ],
    readingTime: 3,
    status: "published",
    group: "Business",
    lastUpdated: "2026-10-07",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Clinic Door in the Business category. Click the card to open the editor.",
          },
        ],
        screenshot: {
          src: "apps/clinic-door/01.png",
          alt: "The App Store with the Clinic Door card in the Business category",
          todo: "App Store, Business chip, Clinic Door card highlighted.",
        },
      },
      {
        title: "Choose the clinic",
        blocks: [
          {
            type: "p",
            text: "In Create: Clinic Door, name the app (for example Clinic 12 door) and pick The clinic at this door. The list only offers clinics this Space's screens can show: the Space's own clinics and organisation-wide ones. The preview on the right switches from a sample clinic to the real one, live.",
          },
        ],
        screenshot: {
          src: "apps/clinic-door/02.png",
          alt: "The Clinic Door editor with the clinic chosen and a live preview",
          todo: "Clinic Door editor: clinic selected, live preview of the door.",
        },
      },
      {
        title: "Choose what the door shows",
        blocks: [
          {
            type: "ul",
            items: [
              "Language: Both (English with Arabic), English, or Arabic. In Arabic the layout runs right to left.",
              "Orientation: follow the screen, or force landscape or portrait.",
              "Next patients, and How many next patients (1 to 5).",
              "Waiting count and average wait.",
              "Delay notice, and Show the delay from (5 to 30 minutes late).",
              "Doctor photo: the photo your hospital system sends, or the doctor's initials.",
            ],
          },
        ],
      },
      {
        title: "Set the calling sound",
        blocks: [
          {
            type: "p",
            text: "Tone when a patient is called plays a short chime when a ticket is called or recalled. Spoken announcement (English) reads out the Announcement after the tone; `{ticket}` becomes the ticket number and `{room}` the clinic's room, for example Ticket {ticket}, please enter clinic {room}.",
          },
          {
            type: "note",
            tone: "info",
            text: "Arabic is shown on the door but not spoken: the spoken voice is English only for now. Screens only play sound if their speakers are on and the screen's volume is up.",
          },
        ],
      },
      {
        title: "Match your colours, save and assign",
        blocks: [
          {
            type: "p",
            text: "Under Colours set the Header, Accent, Background and Text colours. Click Save Changes, then assign the app to the screen at that clinic's door.",
          },
          { type: "steps-ref", label: "See: Assign content to a screen", to: "/screens/assign-content" },
          {
            type: "note",
            tone: "info",
            text: "If a screen loses its connection it keeps showing the last state it received and catches up when it reconnects. Pointing an existing app at another clinic never shows the old clinic's patients on the new door.",
          },
        ],
        screenshot: {
          src: "apps/clinic-door/03.png",
          alt: "A clinic door in portrait with a patient called",
          caption: "A patient called: the ticket is highlighted with Please enter.",
          todo: "/app-runtime/clinic-door?demo=1&status=called in portrait 1080x1920.",
        },
      },
    ],
    related: [
      { label: "Clinic Flow: clinic door screens", to: "/modules/clinic-flow" },
      { label: "Clinics API for hospital systems", to: "/developers/clinics-api" },
      { label: "How apps work", to: "/apps/add-an-app" },
    ],
    keywords: ["clinic door", "clinic", "door screen", "doctor", "patient", "hospital", "now serving", "ticket", "healthcare", "outpatient"],
  },

  {
    slug: "power-bi",
    category: "apps",
    route: "/apps/power-bi",
    title: "Power BI",
    summary: "Show a Power BI “Publish to web” report full-screen, with auto-refresh.",
    intro:
      "The Power BI app shows a report or dashboard you’ve published with Power BI’s Publish to web, full-screen, and can reload it on a schedule so the numbers stay current.",
    prerequisites: [
      "A Power BI report published with File → Publish to web (public). Anyone with that link can see the report.",
      "Screens with a modern browser engine (Chrome / Chromium 80 or newer).",
    ],
    readingTime: 2,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Power BI in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/power-bi/01.png",
          alt: "The app store with Power BI in the Business group",
          todo: "Apps store, Business category, Power BI card highlighted.",
        },
      },
      {
        title: "Paste the Publish to web link",
        blocks: [
          {
            type: "p",
            text: "In the Power BI Service choose File → Publish to web (public) and copy the Link. Paste it into Publish to web link — the editor confirms “Connected · publish-to-web link ✓”. Optionally enter a Report page to open on a specific page.",
          },
          {
            type: "note",
            tone: "warning",
            text: "“Publish to web” makes the report public — no sign-in is needed to view it. Don’t publish reports with confidential data.",
          },
        ],
        screenshot: {
          src: "apps/power-bi/02.png",
          alt: "The Power BI editor with the Publish to web link field",
          todo: "Power BI editor open with the Publish to web link field empty (no neutral published report available), so the preview is empty.",
        },
      },
      {
        title: "Set fit, refresh and interaction",
        blocks: [
          {
            type: "p",
            text: "Choose the Fit mode — Fill the zone, or Fit (letterbox 16:9) with a Background colour around it — and Auto-refresh (Off, or every 5, 15 or 30 minutes, or every hour). Leave Interactive off for passive signage; turn it on for a touch screen where viewers may use filters and drill-downs.",
          },
          {
            type: "note",
            tone: "info",
            text: "Power BI needs Chrome / Chromium 80 or newer. Recent Android players, Windows and current smart TVs are fine; older boxes may show a blank or error screen.",
          },
        ],
        screenshot: {
          src: "apps/power-bi/03.png",
          alt: "Fit mode, Auto-refresh, Background colour and Interactive settings",
          caption: "Choose how the report fits and how often it reloads.",
          todo: "Power BI editor scrolled to Fit mode / Auto-refresh / Background colour / Interactive and the compatibility note.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Google Sheets", to: "/apps/google-sheets" },
      { label: "Google Slides", to: "/apps/google-slides" },
    ],
    keywords: ["power bi", "dashboard", "report", "publish to web", "kpi", "business intelligence"],
  },

  {
    slug: "wayfinding",
    category: "apps",
    route: "/apps/wayfinding",
    title: "Wayfinding",
    summary: "Put a touch-screen wayfinding kiosk for one of your Wayfinding sites on a screen.",
    intro:
      "The Wayfinding app is the on-screen kiosk of the Wayfinding module: each screen shows “you are here” and routes visitors to any place in your venue, step-free if needed, in Arabic and English. You build the site — floor plans, places and walkways — in the module; this app puts it on a screen.",
    prerequisites: [
      "The Wayfinding module on your organisation. Without it, the Wayfinding app doesn’t appear in the store.",
      "A site set up in Modules → Wayfinding, with each kiosk screen placed on its map.",
    ],
    readingTime: 2,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Wayfinding in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/wayfinding/01.png",
          alt: "The app store with Wayfinding in the Business group",
          todo: "Apps store, Business category, Wayfinding card highlighted.",
        },
      },
      {
        title: "Choose the site and configure the kiosk",
        blocks: [
          {
            type: "p",
            text: "In Create: Wayfinding, pick the Site. Optionally choose a Fallback kiosk for screens not placed on the map, up to eight Popular places for the home screen, the Map rotation, the Language options, the top bar (logo, title, clock, info button with Wi-Fi) and the content that plays under the map or as a screen saver.",
          },
          {
            type: "steps-ref",
            label: "See: Wayfinding kiosk settings in detail",
            to: "/modules/wayfinding-kiosk",
          },
        ],
        screenshot: {
          src: "apps/wayfinding/02.png",
          alt: "The Wayfinding app editor with the Site picker and a live kiosk preview",
          todo: "Wayfinding app editor open: Site chosen, Popular places, preview of the kiosk.",
        },
      },
      {
        title: "Save and assign to the kiosk screens",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to every kiosk screen of the site. Each screen routes visitors from where it’s placed on the map.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "Wayfinding module", to: "/modules/wayfinding" },
      { label: "Wayfinding kiosk", to: "/modules/wayfinding-kiosk" },
      { label: "Campus Directory", to: "/apps/campus-directory" },
    ],
    keywords: ["wayfinding", "kiosk", "map", "directions", "you are here", "step-free", "arabic"],
  },

  // ── Hotel ──────────────────────────────────────────────────────────────────
  {
    slug: "hotel-welcome",
    category: "apps",
    route: "/apps/hotel-welcome",
    title: "Hotel Welcome Board",
    summary: "Greet arriving guests, groups and events by name, in English and Arabic.",
    intro:
      "The Hotel Welcome Board greets today’s guests, groups and events by name — one at a time as a spotlight, or as a board listing the day’s arrivals — with your property name and branding, in English, Arabic or both.",
    readingTime: 2,
    status: "draft",
    group: "Hotel",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Hotel Welcome Board in the Hotel category.",
          },
        ],
        screenshot: {
          src: "apps/hotel-welcome/01.png",
          alt: "The app store with Hotel Welcome Board in the Hotel group",
          todo: "Apps store, Hotel category, Hotel Welcome Board card highlighted.",
        },
      },
      {
        title: "Set the greeting",
        blocks: [
          {
            type: "p",
            text: "In Create: Hotel Welcome Board, under Greeting pick the Language — English, Arabic, or Both (English with Arabic beneath) — then the Property name, the Greeting (for example “Welcome”) and an optional Message. The Arabic fields appear when Arabic is on.",
          },
        ],
        screenshot: {
          src: "apps/hotel-welcome/02.png",
          alt: "The Hotel Welcome Board editor with the Greeting section and a live preview",
          todo: "Hotel Welcome Board editor open: Greeting section + preview. Use placeholder guest names only.",
        },
      },
      {
        title: "Add today’s guests",
        blocks: [
          {
            type: "p",
            text: "Under Today’s guests use + Add guest for each arrival: Guest or group, a Detail (for example the event), Where and When (free text). Untick Show to hide an entry without deleting it. With no entries, the board still shows your greeting and branding.",
          },
        ],
        screenshot: {
          src: "apps/hotel-welcome/03.png",
          alt: "The Today’s guests list with Add guest",
          caption: "Add each guest or group arriving today.",
          todo: "Hotel Welcome Board editor scrolled to Today’s guests with two entries (placeholder names).",
        },
      },
      {
        title: "Choose the layout and look",
        blocks: [
          {
            type: "p",
            text: "Under Appearance choose the Layout — Spotlight (one guest at a time) or Board (a list of today’s arrivals) — the Orientation, Rows per page, and whether to Show date and Show time. Pick a Background (Solid colour or Photograph, with Darken the photo for readability), the Text colour and Accent, and your Logo.",
          },
        ],
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to your lobby or entrance screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Hotel Directory", to: "/apps/hotel-directory" },
      { label: "Prayer Time", to: "/apps/prayer-time" },
    ],
    keywords: ["hotel welcome", "welcome board", "guests", "arrivals", "greeting", "events", "arabic"],
  },
];
